import React, { useEffect, useState } from 'react';
import percentageTaxAPI from '../api/percentageTaxAPI';
import Button from './Button';

const peso = (n) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(n || 0));

const PercentageTaxQuarterSummary = ({ clientId, initialQuarter, initialFiscalYear }) => {
  const [quarter, setQuarter] = useState(initialQuarter || 1);
  const [fiscalYear, setFiscalYear] = useState(initialFiscalYear || new Date().getFullYear());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [summary, setSummary] = useState(null);
  const [excess, setExcess] = useState(0);
  const [savingExcess, setSavingExcess] = useState(false);

  const load = async () => {
    // require all inputs
    if (!clientId || !quarter || !fiscalYear) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const [summaryResult, adjustmentResult] = await Promise.allSettled([
        percentageTaxAPI.getQuarterSummary(clientId, { quarter, fiscalYear }),
        percentageTaxAPI.getQuarterAdjustment(clientId, { quarter, fiscalYear }),
      ]);

      if (summaryResult.status !== 'fulfilled') {
        throw summaryResult.reason;
      }

      const payload = summaryResult.value?.data?.data ?? summaryResult.value?.data ?? null;
      const adjustment = adjustmentResult.status === 'fulfilled'
        ? (adjustmentResult.value?.data?.data ?? adjustmentResult.value?.data ?? { excessFromPrevQuarter: 0 })
        : { excessFromPrevQuarter: 0 };

      setSummary(payload);
      setExcess(Number(adjustment?.excessFromPrevQuarter || 0));
    } catch (e) {
      setError(e?.toString?.() || 'Failed to load quarter summary');
    } finally {
      setLoading(false);
    }
  };

  // sync incoming props (so parent period selection can drive the child)
  useEffect(() => {
    // only update if a non-null value is provided
    if (initialQuarter !== undefined && initialQuarter !== null && initialQuarter !== quarter) {
      setQuarter(initialQuarter);
    }
  }, [initialQuarter]);

  useEffect(() => {
    if (initialFiscalYear !== undefined && initialFiscalYear !== null && initialFiscalYear !== fiscalYear) {
      setFiscalYear(initialFiscalYear);
    }
  }, [initialFiscalYear]);

  useEffect(() => {
    if (!clientId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, quarter, fiscalYear]);

  const saveExcess = async () => {
    try {
      setSavingExcess(true);
      setError('');

      await percentageTaxAPI.saveQuarterAdjustment(clientId, {
        quarter,
        fiscalYear,
        excessFromPrevQuarter: Number(excess || 0),
        notes: '',
      });

      await load();
    } catch (e) {
      setError(e?.toString?.() || 'Failed to save excess');
    } finally {
      setSavingExcess(false);
    }
  };

  const salesTaxDue = summary?.sales?.totalTaxDue || 0;
  const incomeTaxDue = summary?.income?.totalTaxDue || 0;
  const tax2306 = summary?.form2306?.totalTaxWithheld || 0;

  const computedPayable = salesTaxDue - incomeTaxDue - tax2306 - Number(excess || 0);
  const computedCarryOver = computedPayable < 0 ? Math.abs(computedPayable) : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-sm font-medium text-ink-body">Fiscal year</label>
          <select
            className="border border-ink-line rounded px-3 py-2 w-40 text-sm mt-1 block focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(parseInt(e.target.value))}
          >
            {percentageTaxAPI.getFiscalYearOptions(3).map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-ink-body">Quarter</label>
          <select
            className="border border-ink-line rounded px-3 py-2 w-48 text-sm mt-1 block focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            value={quarter}
            onChange={(e) => setQuarter(parseInt(e.target.value))}
          >
            {percentageTaxAPI.getQuarterOptions().map((q) => (
              <option key={q.value} value={q.value}>
                {q.label}
              </option>
            ))}
          </select>
        </div>

        <Button type="button" onClick={load} disabled={loading} loading={loading} variant="outline" size="md">
          Refresh
        </Button>
      </div>

      {error ? <div className="text-danger-500 text-sm">{error}</div> : null}

      {/* Excess from Previous Quarter Input */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 max-w-lg">
        <label className="block text-sm font-medium text-ink mb-2">
          Excess from previous quarter
        </label>
        <p className="text-xs text-ink-body mb-3">
          Enter the excess tax credit from the previous quarter to be deducted from this quarter's tax payable.
        </p>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-ink-body text-sm">₱</span>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={excess}
                onChange={(e) => setExcess(e.target.value)}
                className="pl-8 w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
              />
            </div>
          </div>
          <Button type="button" onClick={saveExcess} disabled={savingExcess} loading={savingExcess} variant="primary" size="md">
            Save
          </Button>
        </div>
      </div>

      {/* Neutral inputs, one highlighted answer — matches VAT Management’s Excess tab */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
          <div className="text-xs text-ink-body">Sales</div>
          <div className="text-lg font-medium text-ink tabular-nums">{peso(salesTaxDue)}</div>
        </div>

        <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
          <div className="text-xs text-ink-body">Income</div>
          <div className="text-lg font-medium text-ink tabular-nums">{peso(incomeTaxDue)}</div>
        </div>

        <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
          <div className="text-xs text-ink-body">Form 2306</div>
          <div className="text-lg font-medium text-ink tabular-nums">{peso(tax2306)}</div>
        </div>

        <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
          <div className="text-xs text-ink-body">Excess prev qtr</div>
          <div className="text-lg font-medium text-ink tabular-nums">{peso(excess)}</div>
        </div>

        <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <div className="text-xs text-primary-700">Payable</div>
          <div className="text-lg font-medium text-primary-700 tabular-nums">{peso(computedPayable)}</div>
          {computedCarryOver > 0 && (
            <div className="text-xs text-primary-700/70 mt-1">Carry-over: {peso(computedCarryOver)}</div>
          )}
        </div>
      </div>

      <div className="text-xs text-ink-body font-mono">
        Formula: Sales − Income − Form 2306 − Excess = Percentage Tax Payable
      </div>
    </div>
  );
};

export default PercentageTaxQuarterSummary;
